import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { filter, firstValueFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as L from 'leaflet';
import { AuthorizedApiService } from '@core';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { CompaniesRecord } from '../../organization/companies-records/companies-record.interface';
import { EmployeeWithLaborConfigResponse } from '../../organization/employees/employee.interface';
import { MobileLogRecord } from './mobile-log.interface';

interface CompaniesResponse {
  data?: CompaniesRecord[];
  items?: CompaniesRecord[];
  results?: CompaniesRecord[];
  companiesRecords?: CompaniesRecord[];
}

interface OrgUnitRecord {
  orgUnitId: string;
  name: string;
}

interface OrgUnitsResponse {
  data?: OrgUnitRecord[];
  items?: OrgUnitRecord[];
  results?: OrgUnitRecord[];
  orgUnits?: OrgUnitRecord[];
}

type DiagnosticTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

interface MobileLogDiagnosticItem {
  icon: string;
  title: string;
  description: string;
  tone: DiagnosticTone;
  previous?: string;
  current?: string;
}

interface MobileLogDeviceFact {
  icon: string;
  label: string;
  value: string;
}

interface MobileLogMetadataInsight {
  icon: string;
  title: string;
  description: string;
  tone: DiagnosticTone;
  trigger: string;
  items: MobileLogDiagnosticItem[];
  deviceFacts: MobileLogDeviceFact[];
}

@Component({
  selector: 'app-mobile-log-detail',
  standalone: true,
  imports: [
    CommonModule,
    BreadcrumbComponent,
    LoadingStateComponent,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    TranslateModule
],
  templateUrl: './mobile-log-detail.component.html',
  styleUrl: './mobile-log-detail.component.scss',
})
export class MobileLogDetailComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('mobileLogMap') private mapRef?: ElementRef<HTMLDivElement>;

  private readonly apiService = inject(AuthorizedApiService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  isLoading = true;
  selectedMobileLogId: string | null = null;
  selectedMobileLog: MobileLogRecord | null = null;
  private map: L.Map | null = null;
  private marker: L.Marker | null = null;

  readonly readAccess = { resource: 'mobileLogs', action: 'read' } as const;

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        void this.syncWithRoute();
      });

    void this.syncWithRoute();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.renderMap(), 0);
  }

  ngOnDestroy(): void {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  get detailTitle(): string {
    return this.selectedMobileLog?.employeeLabel || this.selectedMobileLog?.employeeNumber || this.t('mobileLogsCatalog.detail.title');
  }

  get detailDescription(): string {
    return this.selectedMobileLog?.deviceCreatedAtLabel
      ? this.t('mobileLogsCatalog.detail.descriptionWithDate', { date: this.selectedMobileLog.deviceCreatedAtLabel })
      : this.t('mobileLogsCatalog.detail.description');
  }

  get hasLocation(): boolean {
    return this.selectedMobileLog?.location?.lat !== null
      && this.selectedMobileLog?.location?.lat !== undefined
      && this.selectedMobileLog?.location?.lon !== null
      && this.selectedMobileLog?.location?.lon !== undefined;
  }

  get hasMetadata(): boolean {
    return !!this.selectedMobileLog?.metadata && Object.keys(this.selectedMobileLog.metadata).length > 0;
  }

  get metadataInsight(): MobileLogMetadataInsight | null {
    const metadata = this.asRecord(this.selectedMobileLog?.metadata);
    if (!metadata) {
      return null;
    }

    const items: MobileLogDiagnosticItem[] = [];
    const permissions = this.asRecord(metadata['permissions']);
    const previous = this.asRecord(permissions?.['previous']);
    const current = this.asRecord(permissions?.['current']);
    const diff = this.asRecord(permissions?.['diff']);
    const tracking = this.asRecord(metadata['tracking']);
    const batteryProtection = this.asRecord(metadata['batteryProtection']);

    const servicesEnabled = this.booleanValue(current?.['servicesEnabled']);
    const previousServicesEnabled = this.booleanValue(previous?.['servicesEnabled']);
    const servicesChanged = this.booleanValue(diff?.['servicesChanged']) === true
      || (servicesEnabled !== null && previousServicesEnabled !== null && servicesEnabled !== previousServicesEnabled);
    if (servicesEnabled !== null) {
      items.push({
        icon: servicesEnabled ? 'location_on' : 'location_off',
        title: this.t(servicesEnabled
          ? 'mobileLogsCatalog.metadataInterpretation.locationServicesOn'
          : 'mobileLogsCatalog.metadataInterpretation.locationServicesOff'),
        description: this.t(servicesChanged
          ? (servicesEnabled
            ? 'mobileLogsCatalog.metadataInterpretation.locationServicesEnabledDescription'
            : 'mobileLogsCatalog.metadataInterpretation.locationServicesDisabledDescription')
          : 'mobileLogsCatalog.metadataInterpretation.noServiceChange'),
        tone: servicesEnabled ? 'success' : 'danger',
        ...this.transitionLabels(previousServicesEnabled, servicesEnabled, servicesChanged),
      });
    }

    this.appendPermissionDiagnostic(items, {
      icon: 'my_location',
      titleKey: 'mobileLogsCatalog.metadataInterpretation.foregroundPermission',
      previous: this.stringValue(previous?.['foregroundStatus']),
      current: this.stringValue(current?.['foregroundStatus']),
      changed: this.booleanValue(diff?.['foregroundChanged']),
      revoked: this.booleanValue(diff?.['foregroundRevoked']),
    });
    this.appendPermissionDiagnostic(items, {
      icon: 'phonelink_lock',
      titleKey: 'mobileLogsCatalog.metadataInterpretation.backgroundPermission',
      previous: this.stringValue(previous?.['backgroundStatus']),
      current: this.stringValue(current?.['backgroundStatus']),
      changed: this.booleanValue(diff?.['backgroundChanged']),
      revoked: this.booleanValue(diff?.['backgroundRevoked']),
    });

    const precise = this.booleanValue(current?.['precise']);
    const previousPrecise = this.booleanValue(previous?.['precise']);
    const preciseChanged = this.booleanValue(diff?.['preciseChanged']) === true
      || (precise !== null && previousPrecise !== null && precise !== previousPrecise);
    if (precise !== null) {
      const revoked = this.booleanValue(diff?.['preciseRevoked']) === true || (preciseChanged && !precise);
      items.push({
        icon: precise ? 'gps_fixed' : 'gps_not_fixed',
        title: this.t('mobileLogsCatalog.metadataInterpretation.preciseLocation'),
        description: this.t(precise
          ? (preciseChanged
            ? 'mobileLogsCatalog.metadataInterpretation.preciseEnabledDescription'
            : 'mobileLogsCatalog.metadataInterpretation.preciseOnDescription')
          : (revoked
            ? 'mobileLogsCatalog.metadataInterpretation.preciseRevokedDescription'
            : 'mobileLogsCatalog.metadataInterpretation.preciseOffDescription')),
        tone: precise ? 'success' : 'warning',
        ...this.transitionLabels(previousPrecise, precise, preciseChanged),
      });
    }

    if (tracking) {
      const trackingEnabled = this.booleanValue(tracking['employeeTrackingEnabled']);
      const shiftActive = this.booleanValue(tracking['shiftActive']);
      const stopped = this.booleanValue(tracking['stopped']);
      const active = trackingEnabled === true && shiftActive === true && stopped !== true;
      const tone: DiagnosticTone = stopped === true ? 'warning' : active ? 'success' : 'neutral';
      items.push({
        icon: active ? 'route' : stopped === true ? 'stop_circle' : 'location_disabled',
        title: this.t(active
          ? 'mobileLogsCatalog.metadataInterpretation.trackingActive'
          : stopped === true
            ? 'mobileLogsCatalog.metadataInterpretation.trackingStopped'
            : 'mobileLogsCatalog.metadataInterpretation.trackingInactive'),
        description: this.t(active
          ? 'mobileLogsCatalog.metadataInterpretation.trackingActiveDescription'
          : stopped === true
            ? 'mobileLogsCatalog.metadataInterpretation.trackingStoppedDescription'
            : 'mobileLogsCatalog.metadataInterpretation.trackingInactiveDescription'),
        tone,
      });
    }

    if (batteryProtection) {
      const batteryCurrent = this.asRecord(batteryProtection['current']);
      const lowPowerMode = this.booleanValue(batteryCurrent?.['lowPowerMode']);
      const optimization = this.booleanValue(batteryCurrent?.['batteryOptimizationEnabled']);
      const restricted = lowPowerMode === true || optimization === true;
      items.push({
        icon: restricted ? 'battery_alert' : 'battery_saver',
        title: this.t(restricted
          ? 'mobileLogsCatalog.metadataInterpretation.batteryRestricted'
          : 'mobileLogsCatalog.metadataInterpretation.batteryNormal'),
        description: this.t(restricted
          ? 'mobileLogsCatalog.metadataInterpretation.batteryRestrictedDescription'
          : 'mobileLogsCatalog.metadataInterpretation.batteryNormalDescription'),
        tone: restricted ? 'warning' : 'success',
      });
    }

    const deviceFacts = this.buildDeviceFacts(metadata);
    const hasRevokedPermission = items.some((item) => item.tone === 'danger');
    const hasWarning = items.some((item) => item.tone === 'warning');
    const hasGrantedTransition = items.some((item) => item.tone === 'success' && !!item.previous && !!item.current);
    const headline = hasRevokedPermission
      ? { icon: 'report_problem', tone: 'danger' as const, key: 'attentionRequired' }
      : hasWarning
        ? { icon: 'warning_amber', tone: 'warning' as const, key: 'changesNeedReview' }
        : hasGrantedTransition
          ? { icon: 'verified_user', tone: 'success' as const, key: 'permissionsGranted' }
          : { icon: 'phone_android', tone: 'info' as const, key: 'deviceStatus' };

    return {
      icon: headline.icon,
      tone: headline.tone,
      title: this.t(`mobileLogsCatalog.metadataInterpretation.${headline.key}`),
      description: this.t(`mobileLogsCatalog.metadataInterpretation.${headline.key}Description`),
      trigger: this.translateTrigger(this.stringValue(this.asRecord(metadata['trigger'])?.['type'])),
      items,
      deviceFacts,
    };
  }

  async goBackToList(): Promise<void> {
    await this.router.navigate(['/attendance/mobile-logs'], { queryParamsHandling: 'preserve' });
  }

  private async syncWithRoute(): Promise<void> {
    const mobileLogId = this.route.snapshot.paramMap.get('mobileLogId');
    if (!mobileLogId) {
      this.selectedMobileLogId = null;
      this.selectedMobileLog = null;
      this.isLoading = false;
      return;
    }

    await this.loadMobileLog(mobileLogId);
  }

  private async loadMobileLog(mobileLogId: string): Promise<void> {
    this.isLoading = true;
    try {
      const response = await firstValueFrom(this.apiService.get<MobileLogRecord>(`mobile/logs/${mobileLogId}`));
      this.selectedMobileLogId = response.mobileLogId;
      const normalizedLog = this.normalizeMobileLog(response);
      this.selectedMobileLog = await this.enrichDisplayNames(normalizedLog);
      this.renderMap();
    } catch (error) {
      console.error('Error loading mobile log detail', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('mobileLogsCatalog.notifications.loadDetailError')),
        'bottom',
        'center'
      );
      void this.router.navigate(['/attendance/mobile-logs'], { queryParamsHandling: 'preserve' });
    } finally {
      this.isLoading = false;
    }
  }

  private normalizeMobileLog(item: Partial<MobileLogRecord>): MobileLogRecord {
    const entityType = this.normalizeText(item.entityType) || '-';
    const eventType = this.normalizeText(item.eventType) || '-';
    const source = this.normalizeText(item.source) || '-';
    const status = this.normalizeText(item.status) || '-';
    const reasonCode = this.normalizeText(item.reasonCode) || '-';
    const isActive = item.isActive !== false;
    const lat = item.location?.lat ?? null;
    const lon = item.location?.lon ?? null;
    const acc = item.location?.acc ?? null;

    return {
      mobileLogId: item.mobileLogId || '',
      center: item.center || '',
      uid: item.uid || '-',
      userId: item.userId || null,
      employeeId: item.employeeId || null,
      employeeNumber: item.employeeNumber || '-',
      employeeName: item.employeeName || null,
      companiesRecordId: item.companiesRecordId || null,
      orgUnitId: item.orgUnitId || null,
      orgPath: item.orgPath || null,
      entityType,
      eventType,
      source,
      actionType: item.actionType || null,
      status,
      reasonCode,
      description: item.description || null,
      deviceCreatedAt: item.deviceCreatedAt || null,
      location: item.location || null,
      metadata: item.metadata || null,
      isActive,
      createdAt: item.createdAt || null,
      updatedAt: item.updatedAt || null,
      employeeLabel: item.employeeName || item.employeeNumber || item.employeeId || '-',
      companyName: item.companiesRecordId || '-',
      orgUnitName: item.orgUnitId || '-',
      entityTypeLabel: this.translateOption('mobileLogsCatalog.entityTypes', entityType),
      eventTypeLabel: this.translateOption('mobileLogsCatalog.eventTypes', eventType),
      sourceLabel: this.translateOption('mobileLogsCatalog.sources', source),
      statusLabel: this.translateOption('mobileLogsCatalog.statuses', status),
      reasonCodeLabel: this.translateOption('mobileLogsCatalog.reasonCodes', reasonCode),
      deviceCreatedAtLabel: this.formatDateTime(item.deviceCreatedAt),
      createdAtLabel: this.formatDateTime(item.createdAt),
      updatedAtLabel: this.formatDateTime(item.updatedAt),
      activeLabel: isActive ? this.t('common.status.active') : this.t('common.status.inactive'),
      coordinatesLabel: lat !== null && lon !== null ? `${lat.toFixed(6)}, ${lon.toFixed(6)}` : '-',
      accuracyLabel: acc !== null && acc !== undefined ? `${acc} m` : '-',
    };
  }

  private async enrichDisplayNames(log: MobileLogRecord): Promise<MobileLogRecord> {
    const [employeeName, companyName, orgUnitName] = await Promise.all([
      log.employeeName ? Promise.resolve(log.employeeName) : this.loadEmployeeName(log.employeeId || ''),
      this.loadCompanyName(log.companiesRecordId || ''),
      this.loadOrgUnitName(log.orgUnitId || ''),
    ]);

    return {
      ...log,
      employeeLabel: employeeName || log.employeeLabel || log.employeeNumber || log.employeeId || '-',
      companyName: companyName || log.companyName || '-',
      orgUnitName: orgUnitName || log.orgUnitName || '-',
    };
  }

  private async loadEmployeeName(employeeId: string): Promise<string> {
    if (!employeeId) {
      return '';
    }
    try {
      const response = await firstValueFrom(
        this.apiService.get<EmployeeWithLaborConfigResponse>(`employees/${employeeId}/with-operational-config`)
      );
      return response?.entity?.fullName || response?.entity?.employeeNumber || '';
    } catch (error) {
      console.error('Error loading employee name for mobile log', error);
      return '';
    }
  }

  private async loadCompanyName(companiesRecordId: string): Promise<string> {
    if (!companiesRecordId) {
      return '';
    }
    try {
      const response = await firstValueFrom(
        this.apiService.get<CompaniesRecord[] | CompaniesResponse>('companies-records')
      );
      const company = this.extractCompanies(response).find((item) => item.companiesRecordId === companiesRecordId);
      return company?.name || company?.code || '';
    } catch (error) {
      console.error('Error loading company name for mobile log', error);
      return '';
    }
  }

  private async loadOrgUnitName(orgUnitId: string): Promise<string> {
    if (!orgUnitId) {
      return '';
    }
    try {
      const response = await firstValueFrom(this.apiService.get<OrgUnitRecord[] | OrgUnitsResponse>('org-units'));
      const orgUnit = this.extractOrgUnits(response).find((item) => item.orgUnitId === orgUnitId);
      return orgUnit?.name || '';
    } catch (error) {
      console.error('Error loading org unit name for mobile log', error);
      return '';
    }
  }

  private extractCompanies(response: CompaniesRecord[] | CompaniesResponse): CompaniesRecord[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.items || response.data || response.results || response.companiesRecords || [];
  }

  private extractOrgUnits(response: OrgUnitRecord[] | OrgUnitsResponse): OrgUnitRecord[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.items || response.data || response.results || response.orgUnits || [];
  }

  private renderMap(): void {
    const container = this.mapRef?.nativeElement;
    if (!container || !this.hasLocation || !this.selectedMobileLog?.location) {
      return;
    }

    const lat = this.selectedMobileLog.location.lat!;
    const lon = this.selectedMobileLog.location.lon!;
    const latLng: L.LatLngTuple = [lat, lon];

    if (!this.map) {
      this.map = L.map(container, {
        zoomControl: true,
        attributionControl: true,
      }).setView(latLng, 16);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap contributors',
      }).addTo(this.map);
    }

    if (this.marker) {
      this.marker.remove();
    }

    this.marker = L.marker(latLng).addTo(this.map);
    this.marker.bindPopup(`
      <div class="mobile-log-map-popup">
        <strong>${this.selectedMobileLog.employeeLabel || this.selectedMobileLog.employeeNumber || '-'}</strong>
        <span>${this.selectedMobileLog.deviceCreatedAtLabel}</span>
        <span>${this.selectedMobileLog.coordinatesLabel}</span>
        <span>${this.selectedMobileLog.accuracyLabel}</span>
      </div>
    `);
    this.map.setView(latLng, Math.max(this.map.getZoom(), 16));
    setTimeout(() => this.map?.invalidateSize(), 0);
  }

  private formatDateTime(value?: string | null): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const locale = this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(date);
  }

  private normalizeText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private appendPermissionDiagnostic(
    items: MobileLogDiagnosticItem[],
    config: {
      icon: string;
      titleKey: string;
      previous: string;
      current: string;
      changed: boolean | null;
      revoked: boolean | null;
    }
  ): void {
    if (!config.current) {
      return;
    }

    const changed = config.changed === true || (!!config.previous && config.previous !== config.current);
    const granted = config.current.toLowerCase() === 'granted';
    const revoked = config.revoked === true || (changed && !granted && config.previous.toLowerCase() === 'granted');
    const descriptionKey = revoked
      ? 'permissionRevokedDescription'
      : granted
        ? (changed ? 'permissionGrantedDescription' : 'permissionGrantedCurrentDescription')
        : 'permissionDeniedDescription';

    items.push({
      icon: config.icon,
      title: this.t(config.titleKey),
      description: this.t(`mobileLogsCatalog.metadataInterpretation.${descriptionKey}`),
      tone: revoked ? 'danger' : granted ? 'success' : 'warning',
      ...(changed && config.previous
        ? {
            previous: this.translatePermissionStatus(config.previous),
            current: this.translatePermissionStatus(config.current),
          }
        : {}),
    });
  }

  private transitionLabels(
    previous: boolean | null,
    current: boolean,
    changed: boolean
  ): Pick<MobileLogDiagnosticItem, 'previous' | 'current'> {
    if (!changed || previous === null) {
      return {};
    }
    return {
      previous: this.booleanStatus(previous),
      current: this.booleanStatus(current),
    };
  }

  private buildDeviceFacts(metadata: Record<string, unknown>): MobileLogDeviceFact[] {
    const client = this.asRecord(metadata['client']);
    const app = this.asRecord(client?.['app']);
    const device = this.asRecord(client?.['device']);
    if (!client || (!app && !device)) {
      return [];
    }

    const deviceName = [this.stringValue(device?.['manufacturer']) || this.stringValue(device?.['brand']), this.stringValue(device?.['modelName'])]
      .filter(Boolean)
      .join(' ');
    const os = [this.stringValue(device?.['osName']) || this.stringValue(app?.['platform']), this.stringValue(device?.['osVersion']) || this.stringValue(app?.['platformVersion'])]
      .filter(Boolean)
      .join(' ');
    const version = [this.stringValue(app?.['nativeApplicationVersion']), this.stringValue(app?.['nativeBuildVersion'])]
      .filter(Boolean)
      .join(' · build ');

    return [
      deviceName ? { icon: 'smartphone', label: this.t('mobileLogsCatalog.metadataInterpretation.device'), value: deviceName } : null,
      os ? { icon: 'android', label: this.t('mobileLogsCatalog.metadataInterpretation.operatingSystem'), value: os } : null,
      version ? { icon: 'apps', label: this.t('mobileLogsCatalog.metadataInterpretation.appVersion'), value: version } : null,
    ].filter((item): item is MobileLogDeviceFact => item !== null);
  }

  private translatePermissionStatus(value: string): string {
    const normalized = value.toLowerCase();
    const key = `mobileLogsCatalog.metadataInterpretation.statuses.${normalized}`;
    const translated = this.t(key);
    return translated === key ? value : translated;
  }

  private translateTrigger(value: string): string {
    if (!value) {
      return this.t('mobileLogsCatalog.metadataInterpretation.unknownTrigger');
    }
    const key = `mobileLogsCatalog.metadataInterpretation.triggers.${value}`;
    const translated = this.t(key);
    return translated === key ? value : translated;
  }

  private booleanStatus(value: boolean): string {
    return this.t(value
      ? 'mobileLogsCatalog.metadataInterpretation.enabled'
      : 'mobileLogsCatalog.metadataInterpretation.disabled');
  }

  private asRecord(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
      ? value as Record<string, unknown>
      : null;
  }

  private stringValue(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private booleanValue(value: unknown): boolean | null {
    return typeof value === 'boolean' ? value : null;
  }

  private translateOption(baseKey: string, value: string): string {
    if (!value || value === '-') {
      return '-';
    }

    const key = `${baseKey}.${value}`;
    const translated = this.t(key);
    return translated !== key ? translated : value;
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 2400,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}
